// MetricsParticles.js — Four key metrics, readable large type + ambient particle field.
// Sticky-scroll section: 120vh outer, 100vh sticky inner, black background.
import { useRef, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, useInView, animate } from 'framer-motion';

const METRICS = [
  { value: 32,    suffix: '+', label: 'languages available' },
  { value: 98,    suffix: '%', label: 'voice match accuracy' },
  { value: 4,     suffix: ' MIN', label: 'avg processing time' },
  { value: 10,    suffix: 'K+', label: 'videos dubbed' },
];

// ─── Ambient particle field (subtle, decorative only) ────────────────────
function useAmbientParticles(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      canvas.width  = canvas.offsetWidth  * dpr;
      canvas.height = canvas.offsetHeight * dpr;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const COUNT = 90;
    const particles = Array.from({ length: COUNT }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.12 * dpr,
      vy: (Math.random() - 0.5) * 0.12 * dpr,
      r:  (Math.random() * 0.8 + 0.4) * dpr,
    }));

    let rafId;
    function draw() {
      const ctx = canvas.getContext('2d');
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
      }

      // Faint proximity connections
      const threshold = 140 * dpr;
      ctx.lineWidth = 0.5 * dpr;
      for (let i = 0; i < COUNT; i++) {
        for (let j = i + 1; j < COUNT; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d2 = dx * dx + dy * dy;
          if (d2 < threshold * threshold) {
            const a = (1 - Math.sqrt(d2) / threshold) * 0.03;
            ctx.strokeStyle = `rgba(255,255,255,${a})`;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Particles
      for (const p of particles) {
        ctx.fillStyle = 'rgba(255,255,255,0.14)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafId = requestAnimationFrame(draw);
    }
    rafId = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(rafId); ro.disconnect(); };
  }, [canvasRef]);
}

// ─── Count-up number ─────────────────────────────────────────────────────
function CountUp({ value, suffix, isInView }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, value, {
      duration: 1.6,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => setDisplay(latest),
    });
    return () => controls.stop();
  }, [isInView, value]);

  return (
    <>
      {Math.round(display)}
      <Box component="span" sx={{ opacity: display >= value * 0.95 ? 1 : 0.4, transition: 'opacity 0.3s' }}>
        {suffix}
      </Box>
    </>
  );
}

// ─── Component ───────────────────────────────────────────────────────────
export default function MetricsParticles() {
  const stickyRef = useRef(null);
  const canvasRef = useRef(null);
  const isInView  = useInView(stickyRef, { amount: 0.35, once: false });

  useAmbientParticles(canvasRef);

  return (
    <Box sx={{ position: 'relative', height: '120vh', background: '#000' }}>
      <Box
        ref={stickyRef}
        sx={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          width: '100%',
          overflow: 'hidden',
          background: '#000',
        }}
      >
        {/* Ambient particle background */}
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            zIndex: 1, opacity: 0.6,
          }}
        />
        {/* Radial vignette */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.75) 92%)',
          pointerEvents: 'none',
        }} />

        {/* Content */}
        <Box sx={{
          position: 'relative', zIndex: 2, height: '100%',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          px: { xs: 3, md: 6 },
        }}>
          {/* Section label */}
          <Box sx={{
            position: 'absolute', top: 40, left: 40,
            pointerEvents: 'none',
          }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 600,
              fontSize: '11px', letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.25)',
            }}>
              05 — By The Numbers
            </Typography>
          </Box>

          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 12 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            style={{ textAlign: 'center', marginBottom: 'clamp(48px, 8vh, 96px)' }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(1rem, 1.8vw, 1.4rem)',
              color: 'rgba(255,255,255,0.35)',
              letterSpacing: '0.04em',
            }}>
              Numbers that speak for themselves.
            </Typography>
          </motion.div>

          {/* Metrics grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: { xs: 6, md: 4 },
            width: '100%',
            maxWidth: 1200,
          }}>
            {METRICS.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 24 }}
                transition={{
                  duration: 0.8,
                  ease: [0.16, 1, 0.3, 1],
                  delay: 0.15 + i * 0.12,
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{
                    fontFamily: 'Inter', fontWeight: 900,
                    fontSize: 'clamp(3rem, 7vw, 6.5rem)',
                    lineHeight: 1,
                    color: '#fff',
                    letterSpacing: '-0.045em',
                    textShadow: '0 0 60px rgba(255,255,255,0.08)',
                    display: 'flex', justifyContent: 'center', alignItems: 'baseline',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    <CountUp value={m.value} suffix={m.suffix} isInView={isInView} />
                  </Typography>
                  <Box sx={{
                    width: 24, height: 1,
                    background: 'rgba(255,255,255,0.25)',
                    mx: 'auto', my: 2.5,
                  }} />
                  <Typography sx={{
                    fontFamily: 'Inter', fontWeight: 500,
                    fontSize: 'clamp(0.7rem, 1vw, 0.82rem)',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.5)',
                  }}>
                    {m.label}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
